import React from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { MdAddPhotoAlternate, MdArrowBack, MdArrowForward, MdClose, MdStar, MdStarBorder } from 'react-icons/md';
import { FONT, imageUrl } from './bogoShared';

export const MAX_IMAGES = 8;
const MAX_MB = 5;
let seq = 0;

// Items are { id, saved?: '/uploads/...', file?: File, preview }. The first item is the cover.
export const savedImages = (paths = []) => paths.filter(Boolean).map((p) => ({ id: `s${seq++}`, saved: p, preview: p }));

// Adds the pictures to a FormData: the order they appear in, and the files for the new ones.
export const appendImages = (body, items) => {
  let n = 0;
  const order = items.map((it) => (it.file ? `new:${n++}` : it.saved));
  body.append('imageOrder', JSON.stringify(order));
  items.forEach((it) => { if (it.file) body.append('product_images', it.file); });
};

// Several pictures for an offer: add many, remove, reorder, choose the cover.
const OfferImages = ({ items, onChange, error, onError }) => {
  const add = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) return;
    const room = MAX_IMAGES - items.length;
    const fresh = [];
    for (const f of picked) {
      if (!f.type.startsWith('image/')) { onError(`“${f.name}” is not an image`); return; }
      if (f.size > MAX_MB * 1024 * 1024) { onError(`“${f.name}” is over ${MAX_MB} MB`); return; }
      fresh.push({ id: `n${seq++}`, file: f, preview: URL.createObjectURL(f) });
    }
    if (fresh.length > room) onError(`Only ${MAX_IMAGES} pictures are allowed. ${room > 0 ? `The first ${room} were added.` : 'Remove one first.'}`);
    else onError('');
    onChange([...items, ...fresh.slice(0, Math.max(room, 0))]);
  };
  const remove = (id) => { onError(''); onChange(items.filter((x) => x.id !== id)); };
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const cover = (i) => { if (i > 0) onChange([items[i], ...items.filter((_, k) => k !== i)]); };

  return (
    <Box>
      <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))' }}>
        {items.map((it, i) => (
          <Box key={it.id} sx={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', border: i === 0 ? '2px solid rgb(255 209 2)' : '1px solid #e2e8f0', background: '#f8fafc' }}>
            <Box component="img" src={it.file ? it.preview : imageUrl(it.preview)} alt="" sx={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', display: 'block' }} />
            {i === 0 && <Box sx={{ position: 'absolute', top: 6, left: 6, background: 'rgb(255 209 2)', color: '#000', borderRadius: '6px', px: 0.75, fontSize: 10.5, fontWeight: 800, fontFamily: FONT }}>COVER</Box>}
            <IconButton size="small" aria-label="remove" onClick={() => remove(it.id)} sx={{ position: 'absolute', top: 4, right: 4, background: 'rgba(255,255,255,.92)', '&:hover': { background: '#fff' } }}><MdClose size={14} /></IconButton>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 0.25, background: '#fff' }}>
              <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)} aria-label="move earlier"><MdArrowBack size={14} /></IconButton>
              <IconButton size="small" disabled={i === 0} onClick={() => cover(i)} aria-label={i === 0 ? 'cover picture' : 'make cover'} title={i === 0 ? 'This is the cover' : 'Make cover'} sx={{ '&.Mui-disabled': { color: 'inherit' } }}>{i === 0 ? <MdStar size={16} color="#ca8a04" /> : <MdStarBorder size={16} color="#94a3b8" />}</IconButton>
              <IconButton size="small" disabled={i === items.length - 1} onClick={() => move(i, 1)} aria-label="move later"><MdArrowForward size={14} /></IconButton>
            </Box>
          </Box>
        ))}
        {items.length < MAX_IMAGES && (
          <Button component="label" sx={{ minHeight: 120, border: '2px dashed #cbd5e1', borderRadius: '12px', color: '#475569', flexDirection: 'column', textTransform: 'none', fontWeight: 700, fontFamily: FONT }}>
            <MdAddPhotoAlternate size={28} />
            Add pictures
            <input hidden multiple type="file" accept="image/*" onChange={add} />
          </Button>
        )}
      </Box>
      <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b', mt: 1 }}>
        {items.length} of {MAX_IMAGES}. The first picture is the cover shown in lists; customers can flip through all of them. JPG or PNG, under {MAX_MB} MB each.
      </Typography>
      {error && <Typography sx={{ color: '#d32f2f', fontSize: 12.5, mt: 0.5 }}>{error}</Typography>}
    </Box>
  );
};

export default OfferImages;

// Read-only gallery for the view pages: a big picture and thumbnails to flip through.
export const ImageGallery = ({ images = [] }) => {
  const [current, setCurrent] = React.useState(0);
  if (!images.length) {
    return <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b', py: 2, textAlign: 'center' }}>No pictures added yet. Edit the offer to add some.</Typography>;
  }
  const shown = images[Math.min(current, images.length - 1)];
  return (
    <Box>
      <Box component="img" src={imageUrl(shown)} alt="" sx={{ width: '100%', maxHeight: 320, objectFit: 'contain', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }} />
      {images.length > 1 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.25 }}>
          {images.map((src, i) => (
            <Box key={src} component="img" src={imageUrl(src)} alt="" onClick={() => setCurrent(i)}
              sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: '8px', cursor: 'pointer', border: i === current ? '2px solid rgb(255 209 2)' : '1px solid #e2e8f0', opacity: i === current ? 1 : 0.75 }} />
          ))}
        </Box>
      )}
      <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b', mt: 1 }}>{images.length} picture{images.length === 1 ? '' : 's'}. The first is the cover.</Typography>
    </Box>
  );
};
